import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please log in.");
      nav("/login");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid Link" sub="This reset link is missing or broken.">
        <div className="space-y-6" data-testid="reset-no-token">
          <div className="flex items-start gap-3 border border-red-500/30 bg-red-500/[0.06] p-5 rounded">
            <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
            <p className="font-body text-white/90 text-sm leading-relaxed">
              This password reset link is invalid. Please request a new one.
            </p>
          </div>
          <Link to="/forgot-password" className="btn-signal w-full h-14 font-head text-lg flex items-center justify-center" data-testid="request-new-link">
            Request New Link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set New Password" sub="Choose a new password for your account.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div>
          <label className="il-label">New Password</label>
          <input
            className="il-input" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required minLength={6}
            data-testid="reset-password" placeholder="••••••••"
          />
        </div>
        <div>
          <label className="il-label">Confirm Password</label>
          <input
            className="il-input" type="password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} required minLength={6}
            data-testid="reset-confirm" placeholder="••••••••"
          />
        </div>
        {error && <p className="font-body text-sm text-red-400" data-testid="reset-error">{error}</p>}
        <button className="btn-signal w-full h-14 font-head text-lg" disabled={loading} data-testid="reset-submit">
          {loading ? "Resetting…" : "Reset Password"}
        </button>
      </form>
      <p className="font-body text-[#94A3B8] mt-6 text-sm">
        <Link to="/login" className="text-[#FFB800] font-700" data-testid="goto-login">Back to Log In →</Link>
      </p>
    </AuthShell>
  );
}
